import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCheck,
  IconCircleDashed,
  IconPlugConnected,
  IconRefresh,
  IconUnlink,
} from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { notifications } from '@mantine/notifications';
import { trpc } from '../lib/trpc';

export type WebhookObject = 'whatsapp_business_account' | 'page' | 'instagram';

/**
 * SuperAdmin app-level webhook-health panel, shared by the WhatsApp, Facebook
 * (Messenger `page`) and Instagram (`instagram`) health pages. It reads the
 * live `GET /{app_id}/subscriptions` probe via `admin.webhookHealth` and lets a
 * SuperAdmin subscribe / re-sync / unsubscribe the app to the webhook object —
 * the tier that actually makes Meta deliver events.
 */
export function WebhookHealthPanel({
  object,
  title,
  objectLabel,
  productNoun,
  igLogin,
}: {
  object: WebhookObject;
  title: string;
  /** Object name shown in the "not subscribed to <x>" alert. */
  objectLabel: string;
  /** Product noun for the unsubscribe warning, e.g. "Messenger". */
  productNoun: string;
  /** For object='instagram': true probes the standalone Instagram app
   *  (graph.instagram.com) instead of the Facebook app's `instagram` object. */
  igLogin?: boolean;
}) {
  const input = { object, igLogin };
  const q = trpc.admin.webhookHealth.read.useQuery(input);
  const utils = trpc.useUtils();
  const [unsubOpen, setUnsubOpen] = useState(false);
  const invalidate = () => utils.admin.webhookHealth.read.invalidate();

  const subscribe = trpc.admin.webhookHealth.subscribe.useMutation({
    onSuccess: (r) => {
      notifications.show({
        message: `Subscribed (${r.subscribedFields.length} fields).`,
        color: 'green',
      });
      void invalidate();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const resync = trpc.admin.webhookHealth.resync.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Refreshed from Meta', color: 'green' });
      void invalidate();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const unsubscribe = trpc.admin.webhookHealth.unsubscribe.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'App-level webhook unsubscribed', color: 'orange' });
      setUnsubOpen(false);
      void invalidate();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2}>{title}</Title>
          <Group gap="xs">
            <Button
              variant="default"
              leftSection={<IconRefresh size={14} />}
              loading={resync.isPending}
              onClick={() => resync.mutate(input)}
            >
              Re-sync
            </Button>
            <Button
              leftSection={<IconPlugConnected size={14} />}
              loading={subscribe.isPending}
              onClick={() => subscribe.mutate(input)}
            >
              Subscribe
            </Button>
            <Button
              variant="light"
              color="red"
              leftSection={<IconUnlink size={14} />}
              onClick={() => setUnsubOpen(true)}
            >
              Unsubscribe
            </Button>
          </Group>
        </Group>
        {q.isPending ? (
          <Skeleton height={200} />
        ) : !q.data ? (
          <Text c="red">{q.error?.message}</Text>
        ) : (
          <Stack gap="md">
            <Card>
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text fw={600}>App ID</Text>
                  <Text c={q.data.appId ? undefined : 'dimmed'}>{q.data.appId ?? '—'}</Text>
                </Group>
                <Group justify="space-between">
                  <Text fw={600}>Verify token configured</Text>
                  <Badge color={q.data.verifyTokenSet ? 'green' : 'red'} variant="light">
                    {q.data.verifyTokenSet ? 'yes' : 'no'}
                  </Badge>
                </Group>
                <Group justify="space-between">
                  <Text fw={600}>App-level verified at</Text>
                  <Text size="sm" c="dimmed">
                    {q.data.appLevelVerifiedAt
                      ? new Date(q.data.appLevelVerifiedAt).toLocaleString()
                      : '—'}
                  </Text>
                </Group>
                <Group justify="space-between">
                  <Text fw={600}>Callback URL</Text>
                  <Text size="sm" c="dimmed">
                    {q.data.callbackUrl ?? '—'}
                  </Text>
                </Group>
                {q.data.rawInbound ? (
                  <Group justify="space-between">
                    <Text fw={600}>Raw POSTs received (24h)</Text>
                    <Text size="sm" c={q.data.rawInbound.count > 0 ? 'green' : 'red'}>
                      {q.data.rawInbound.count}
                      {q.data.rawInbound.lastSeenAt
                        ? ` · last ${new Date(q.data.rawInbound.lastSeenAt).toLocaleString()}`
                        : ''}
                    </Text>
                  </Group>
                ) : null}
              </Stack>
            </Card>

            {q.data.subscribedFields === null ? (
              <Alert color="orange" icon={<IconCircleDashed />} title="Probe failed">
                Could not read <code>/&#123;app_id&#125;/subscriptions</code> — likely the platform
                App Secret is missing or the token is revoked. Configure the Meta App credentials in
                the Configuration page, then click <b>Re-sync</b>.
              </Alert>
            ) : q.data.subscribedFields.length === 0 ? (
              <Alert color="orange" icon={<IconAlertCircle />} title="No subscription">
                The app is not currently subscribed to <code>{objectLabel}</code>. Click{' '}
                <b>Subscribe</b> to subscribe with the full default field list.
              </Alert>
            ) : q.data.missingFields.length > 0 ? (
              <Alert color="orange" icon={<IconAlertCircle />} title="Missing fields">
                Meta is not delivering: {q.data.missingFields.join(', ')}. Click <b>Subscribe</b> to
                re-subscribe with the full default list.
              </Alert>
            ) : (
              <Alert color="green" icon={<IconCheck />}>
                All default fields are subscribed.
              </Alert>
            )}

            {q.data.rawInbound && q.data.subscribedFields && q.data.subscribedFields.length > 0 ? (
              q.data.rawInbound.count === 0 ? (
                <Alert color="orange" icon={<IconAlertCircle />} title="No inbound POSTs received">
                  The subscription is correct, but Meta has not delivered any webhook POST to this
                  endpoint in the last 24h — so the gap is Meta-side delivery, not this app. Confirm
                  the messaging permission has <b>Advanced Access</b>, the app is in <b>Live</b>{' '}
                  mode, and (Instagram) the account has{' '}
                  <b>Settings → Messages → Connected Tools → Allow access to messages</b> enabled.
                </Alert>
              ) : (
                <Alert color="blue" icon={<IconCheck />} title="Meta is delivering">
                  {q.data.rawInbound.count} raw POST(s) reached this endpoint in the last 24h. If
                  chats still don&apos;t appear, events are being dropped after delivery — check the
                  API logs for <code>webhook signature failed</code> (App Secret / proxy altering
                  the body) or <code>no connected page/account matched</code> (entry.id → vendor).
                </Alert>
              )
            ) : null}

            <Card p={0}>
              <Table striped withRowBorders={false}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Default fields</Table.Th>
                    <Table.Th>Subscribed?</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {q.data.defaultFields.map((f) => {
                    const subscribed = q.data.subscribedFields?.includes(f) ?? null;
                    return (
                      <Table.Tr key={f}>
                        <Table.Td>
                          <Text ff="monospace" size="sm">
                            {f}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          {subscribed === null ? (
                            <Badge variant="light">unknown</Badge>
                          ) : subscribed ? (
                            <Badge color="green" variant="light">
                              yes
                            </Badge>
                          ) : (
                            <Badge color="red" variant="light">
                              no
                            </Badge>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Card>
          </Stack>
        )}
      </Stack>

      <Modal
        opened={unsubOpen}
        onClose={() => setUnsubOpen(false)}
        title="Unsubscribe app-level webhook?"
        centered
      >
        <Stack gap="sm">
          <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
            This is a PLATFORM-WIDE change. Every connected vendor will stop receiving {productNoun}{' '}
            inbounds until you re-subscribe. Use this only when rotating Meta App credentials or
            off-boarding from this platform deployment.
          </Alert>
          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={() => setUnsubOpen(false)}>
              Cancel
            </Button>
            <Button
              color="red"
              loading={unsubscribe.isPending}
              onClick={() => unsubscribe.mutate(input)}
            >
              Unsubscribe
            </Button>
          </Group>
        </Stack>
      </Modal>
    </motion.div>
  );
}
